import {useAddBanner, useFocusBanner} from '@github-ui/banner-stack'
import {useMutation, useQueryClient, type QueryKey} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {allWorkflowRunsRoute} from '../routes/all-workflow-runs-route'
import {workflowRunsRoute} from '../routes/workflow-runs-route'
import {workflowsLayoutRoute} from '../routes/workflows-layout-route'
import {updateWorkflowStateInPaginationCache} from './use-workflow-pagination'

// The routes whose data must refresh after a disable/enable mutation. `queryKey[0]` alone
// (the host app name, e.g. `repo`) is shared with unrelated routes when this package is
// mounted into a larger app, so invalidating by that prefix would also force-refetch
// completely unrelated cached queries (e.g. the host app's own root layout query).
const ACTIONS_WORKFLOWS_ROUTE_IDS: string[] = [workflowsLayoutRoute.id, workflowRunsRoute.id, allWorkflowRunsRoute.id]

/**
 * Whether a cached query belongs to this feature's routes and should be refreshed after a
 * disable/enable mutation. Scoping on the route ID (`queryKey[1]`), not just the shared host
 * app name (`queryKey[0]`), keeps this from also invalidating unrelated queries that happen to
 * be cached under the same host app (e.g. the host app's own root layout query).
 */
export function isActionsWorkflowsRouteQueryKey(candidateKey: QueryKey, appName: unknown): boolean {
  return candidateKey[0] === appName && ACTIONS_WORKFLOWS_ROUTE_IDS.includes(String(candidateKey[1]))
}

type WorkflowDisableResponse = {
  message: string
}

/** Disables a workflow and refreshes the active Actions route data. */
export function useWorkflowDisable({owner, repo, queryKey}: {owner: string; repo: string; queryKey: QueryKey}) {
  const queryClient = useQueryClient()
  const addBanner = useAddBanner()
  const focusBanner = useFocusBanner()
  const {mutateAsync} = useMutation<WorkflowDisableResponse, Error, bigint>({
    mutationKey: ['actions-workflows', 'workflow-disable', owner, repo],
    mutationFn: async workflowId => {
      const encodedWorkflowId = encodeURIComponent(workflowId.toString())
      const response = await verifiedFetchJSON(`/${owner}/${repo}/actions/workflows/${encodedWorkflowId}/disable`, {
        method: 'POST',
      })
      const data: WorkflowDisableResponse = await response.json()
      if (!response.ok) throw new Error(data.message)
      return data
    },
    onSuccess: () => {
      // A success banner is not needed for this usecase as the DisabledWorkflowBanner will be added
    },
    onError: error => {
      const id = addBanner({
        title: error.message || 'There was an error disabling this workflow.',
        variant: 'critical',
        dismissible: true,
      })
      requestAnimationFrame(() => focusBanner(id))
    },
    onSettled: async (_, error, workflowId) => {
      if (error) return
      updateWorkflowStateInPaginationCache({
        queryClient,
        owner,
        repo,
        workflowId,
        state: 'disabled_manually',
      })
      await queryClient.invalidateQueries({
        predicate: query => isActionsWorkflowsRouteQueryKey(query.queryKey, queryKey[0]),
      })
    },
  })

  const disableWorkflow = useCallback(
    async (workflowId: bigint): Promise<void> => {
      await mutateAsync(workflowId)
    },
    [mutateAsync],
  )

  return {disableWorkflow}
}
